/**
 * B702 — memberships (B655 a+b+h): the §11 doors over plans, member records
 * and the tenant's membership switches. No screen rides this item; the MCP
 * membrane serves every op here automatically.
 *
 * Who may call (B655 ruling): plan + record WRITES are the back office
 * (STAFF:ADMIN/MANAGER, `entitlement:write`); the counter (STAFF:SELLER —
 * the POS seat) sells, renews and re-issues cards (`order:write`); a
 * CUSTOMER (a B632 session) reads its OWN memberships under the §11 owner
 * rule; the switches are tenant configuration (ADMIN, `channel:write`).
 * Selling through checkout (B709), charging a saved card (B703) and the
 * gate (B704) are their own items.
 */
import { z } from "zod";
import { readOp, writeOp, zEpochMs, zId, zNonNegMoney } from "./common.js";

const zPositiveMoney = z.object({ amount: z.number().int().safe().positive(), currency: z.string().regex(/^[A-Z]{3}$/) }).strict();

const zName = z.string().min(1).max(80);

const zTier = z
  .object({
    tierId: zId,
    name: zName,
    price: zNonNegMoney,
    covers: z
      .object({
        namedAdults: z.union([z.literal(1), z.literal(2)]),
        householdChildren: z
          .object({ max: z.number().int().min(0).max(10), ageBand: z.object({ min: z.number().int().min(0), max: z.number().int().min(0) }).strict().optional() })
          .strict(),
        guestsPerDay: z.number().int().min(0).max(10),
      })
      .strict(),
    admission: z
      .object({ scope: z.enum(["ALL", "LISTED"]), productIds: z.array(zId).max(200).optional(), blackoutCalendarId: zId.optional() })
      .strict(),
    benefits: z
      .object({
        percentOffByCategory: z.record(z.string().min(1).max(40), z.number().int().min(0).max(10_000)),
        guestPasses: z.number().int().min(0).max(100),
        coupons: z.number().int().min(0).max(100),
        parking: z.boolean(),
        earlyHours: z.boolean(),
        events: z.boolean(),
      })
      .strict(),
    eligibility: z
      .object({
        residentZips: z.array(z.string().min(3).max(10)).max(500).optional(),
        seniorAge: z.number().int().min(0).max(120).optional(),
        childAge: z.number().int().min(0).max(25).optional(),
      })
      .strict(),
    fmv: zNonNegMoney.optional(),
    addOns: z.array(z.object({ addOnId: zId, name: zName, price: zNonNegMoney }).strict()).max(10),
  })
  .strict();

const zPlanKind = z.enum(["ANNUAL", "MONTHLY"]);
const zPlanStatus = z.enum(["DRAFT", "ACTIVE", "RETIRED"]);

const zPlan = z
  .object({
    planId: zId,
    name: zName,
    kind: zPlanKind,
    status: zPlanStatus,
    tiers: z.array(zTier),
    createdAt: zEpochMs,
    updatedAt: zEpochMs,
  })
  .strict();

const zNamedAdult = z.object({ name: zName }).strict();

const zCard = z
  .object({
    cardId: zId,
    holder: z.string().min(1),
    issuedAt: zEpochMs,
    validFrom: zEpochMs,
    validUntil: zEpochMs,
    revokedAt: zEpochMs.optional(),
    /** B704 (3) — when a gate admitted this card under the photoId switch; the ID ask stops from then on. */
    idCheckedAt: zEpochMs.optional(),
  })
  .strict();

const zMemberStatus = z.enum(["ACTIVE", "GRACE", "LAPSED", "CANCELLED", "GIFT_UNREDEEMED"]);

/** A member as every door answers it; `status` is the status AT READ TIME (grace and lapse derive from the term). */
const zMember = z
  .object({
    memberNumber: zId,
    planId: zId,
    tierId: zId,
    customerId: zId.optional(),
    email: z.string().optional(),
    zip: z.string().optional(),
    term: z.object({ start: zEpochMs, end: zEpochMs }).strict(),
    status: zMemberStatus,
    namedAdults: z.array(zNamedAdult),
    householdCount: z.number().int().min(0),
    addOns: z.array(zId),
    /** B703 (2d) — `pendingTierId`: a lower tier queued for the next renewal (membership.downgrade). */
    renewal: z.object({ method: z.enum(["MANUAL", "AUTO"]), pendingTierId: zId.optional() }).strict(),
    allowances: z.object({ guestPasses: z.number().int().min(0), coupons: z.number().int().min(0), guestsByDay: z.record(z.string(), z.number().int().min(0)) }).strict(),
    cards: z.array(zCard),
    createdAt: zEpochMs,
    updatedAt: zEpochMs,
    cancelledAt: zEpochMs.optional(),
    cancelReason: z.string().optional(),
  })
  .strict();

const EXAMPLE_TIER = {
  tierId: "family",
  name: "Family",
  price: { amount: 18_900, currency: "USD" },
  covers: { namedAdults: 2, householdChildren: { max: 4, ageBand: { min: 0, max: 17 } }, guestsPerDay: 1 },
  admission: { scope: "ALL" },
  benefits: { percentOffByCategory: { food: 1_000 }, guestPasses: 2, coupons: 4, parking: true, earlyHours: false, events: true },
  eligibility: {},
  fmv: { amount: 4_000, currency: "USD" },
  addOns: [{ addOnId: "flex-guest", name: "Flex guest", price: { amount: 4_900, currency: "USD" } }],
} as const;

const EXAMPLE_PLAN = {
  planId: "annual",
  name: "Harbor Lights Membership",
  kind: "ANNUAL",
  status: "ACTIVE",
  tiers: [EXAMPLE_TIER],
  createdAt: 1_791_000_000_000,
  updatedAt: 1_791_000_000_000,
} as const;

const EXAMPLE_MEMBER = {
  memberNumber: "M12345678",
  planId: "annual",
  tierId: "family",
  customerId: "cust-1",
  email: "ada@example.org",
  term: { start: 1_791_000_000_000, end: 1_822_536_000_000 },
  status: "ACTIVE",
  namedAdults: [{ name: "Ada Lovelace" }, { name: "Charles Babbage" }],
  householdCount: 2,
  addOns: [],
  renewal: { method: "MANUAL" },
  allowances: { guestPasses: 2, coupons: 4, guestsByDay: {} },
  cards: [],
  createdAt: 1_791_000_000_000,
  updatedAt: 1_791_000_000_000,
} as const;

const BACK_OFFICE = ["STAFF:ADMIN", "STAFF:MANAGER"] as const;
const COUNTER = ["STAFF:ADMIN", "STAFF:MANAGER", "STAFF:SELLER"] as const;

/* ----------------------------------------------------------------- plans */

export const membershipPlanCreate = writeOp({
  name: "membership.plan.create",
  permission: "entitlement:write",
  principals: [...BACK_OFFICE],
  fields: { planId: zId, name: zName, kind: zPlanKind, status: zPlanStatus.optional(), tiers: z.array(zTier).min(1).max(12) },
  response: z.object({ plan: zPlan }).strict(),
  exampleRequest: { planId: "annual", name: "Harbor Lights Membership", kind: "ANNUAL", tiers: [EXAMPLE_TIER] },
  exampleResponse: { plan: EXAMPLE_PLAN },
});

export const membershipPlanUpdate = writeOp({
  name: "membership.plan.update",
  permission: "entitlement:write",
  principals: [...BACK_OFFICE],
  fields: { planId: zId, name: zName.optional(), status: zPlanStatus.optional(), tiers: z.array(zTier).min(1).max(12).optional() },
  response: z.object({ plan: zPlan }).strict(),
  exampleRequest: { planId: "annual", status: "RETIRED" },
  exampleResponse: { plan: { ...EXAMPLE_PLAN, status: "RETIRED" } },
});

export const membershipPlanGet = readOp({
  name: "membership.plan.get",
  permission: "catalog:read",
  principals: [...COUNTER, "CUSTOMER"],
  fields: { planId: zId },
  response: z.object({ plan: zPlan.optional() }).strict(),
  exampleRequest: { planId: "annual" },
  exampleResponse: { plan: EXAMPLE_PLAN },
});

/** A buyer sees only plans ON SALE; staff see every plan (drafts and retired included). */
export const membershipPlanList = readOp({
  name: "membership.plan.list",
  permission: "catalog:read",
  principals: [...COUNTER, "CUSTOMER"],
  fields: {},
  response: z.object({ plans: z.array(zPlan) }).strict(),
  exampleRequest: {},
  exampleResponse: { plans: [EXAMPLE_PLAN] },
});

/* --------------------------------------------------------------- members */

export const membershipCreate = writeOp({
  name: "membership.create",
  permission: "order:write",
  principals: [...COUNTER],
  fields: {
    planId: zId,
    tierId: zId,
    namedAdults: z.array(zNamedAdult).min(1).max(2),
    householdCount: z.number().int().min(0).max(10),
    addOns: z.array(zId).max(10).optional(),
    email: z.string().min(3).max(254).optional(),
    zip: z.string().min(3).max(10).optional(),
    gift: z.boolean().optional(),
  },
  response: z.object({ member: zMember }).strict(),
  exampleRequest: { planId: "annual", tierId: "family", namedAdults: [{ name: "Ada Lovelace" }, { name: "Charles Babbage" }], householdCount: 2, email: "ada@example.org" },
  exampleResponse: { member: EXAMPLE_MEMBER },
});

/**
 * The counter renews with no money (the till's money is `membership.sale`).
 * B703 (a) — a CUSTOMER renews its OWN membership online, on the design
 * seat's 2026-10-09 class-A default (Pathik may overrule; reversal = drop
 * CUSTOMER): owner-fenced, priced the way the till prices a renewal and
 * checked against the page's `amount`, charged through B703's
 * PaymentGatewayPort on the connected account (TEST keys only — the B23a
 * wall) — the saved card when the member renews automatically, else the
 * gateway's card page first (`setupUrl`, nothing written). A charge that
 * goes through posts `membership_charge` under the gateway's payment id
 * BEFORE the term extends from the expiry. `termEnd` is the term the page
 * shows: once it has moved the press answers the member and charges
 * nothing. A refused card answers `refused` (a sentence) and writes nothing.
 * The three buyer fields are ignored at the counter.
 */
export const membershipRenew = writeOp({
  name: "membership.renew",
  owner: "customer",
  permission: "order:write",
  principals: [...COUNTER, "CUSTOMER"],
  fields: {
    memberNumber: zId,
    termEnd: zEpochMs.optional(),
    amount: zNonNegMoney.optional(),
    returnUrl: z.string().url().max(2048).optional(),
  },
  response: z.object({ member: zMember.optional(), setupUrl: z.string().optional(), refused: z.string().optional(), charged: zNonNegMoney.optional() }).strict(),
  exampleRequest: {
    memberNumber: "M12345678",
    termEnd: 1_822_536_000_000,
    amount: { amount: 18_900, currency: "USD" },
    returnUrl: "https://store.example.org/me/membership?tenant=t-1&renew=M12345678",
  },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, term: { start: 1_822_536_000_000, end: 1_854_072_000_000 } }, charged: { amount: 18_900, currency: "USD" } },
});

/**
 * B703 (2c-iv) — auto-renew at the counter. ON hands back the gateway's
 * hosted card page (`setupUrl`) and records the words the member agreed to;
 * the member turns AUTO only when the gateway's signed "card saved" arrives.
 * OFF returns the member to manual renewal. No money moves here — the
 * renewal scheduler charges at the expiry (never Stripe Subscriptions).
 */
export const membershipAutorenewSet = writeOp({
  name: "membership.autorenew.set",
  permission: "order:write",
  principals: [...COUNTER],
  fields: {
    memberNumber: zId,
    on: z.boolean(),
    consentText: z.string().max(500).optional(),
    returnUrl: z.string().url().max(2048).optional(),
  },
  response: z.object({ member: zMember, setupUrl: z.string().optional() }).strict(),
  exampleRequest: { memberNumber: "M12345678", on: true, consentText: "Renew my membership each year with this card until I turn it off.", returnUrl: "https://admin.example.org/members/M12345678" },
  exampleResponse: { member: EXAMPLE_MEMBER, setupUrl: "https://checkout.stripe.com/c/pay/cs_test_123" },
});

export const membershipGet = readOp({
  name: "membership.get",
  owner: "customer",
  permission: "entitlement:read",
  principals: [...COUNTER, "CUSTOMER"],
  fields: { memberNumber: zId },
  /** B710 (e) — STAFF reads also carry the household's last visit (the newest admit on any of its cards that no undo reversed; epoch ms). A buyer's own read never does. */
  response: z.object({ member: zMember.optional(), lastVisitAt: z.number().int().nonnegative().optional() }).strict(),
  exampleRequest: { memberNumber: "M12345678" },
  exampleResponse: { member: EXAMPLE_MEMBER, lastVisitAt: 1_791_000_000_000 },
});

/** Staff: the tenant's members (oldest first, `status` narrows); a CUSTOMER: its own. */
export const membershipList = readOp({
  name: "membership.list",
  owner: "customer",
  permission: "entitlement:read",
  principals: [...COUNTER, "CUSTOMER"],
  fields: { status: zMemberStatus.optional(), limit: z.number().int().min(1).max(500).optional() },
  response: z.object({ members: z.array(zMember) }).strict(),
  exampleRequest: {},
  exampleResponse: { members: [EXAMPLE_MEMBER] },
});

/** The counter's lookup: a member number (exact), a named adult's name (contains), an email (exact), or a card id (exact — the till sends a scanned card's id, B710). */
export const membershipSearch = readOp({
  name: "membership.search",
  permission: "entitlement:read",
  principals: [...COUNTER],
  fields: { query: z.string().min(1).max(254) },
  response: z.object({ members: z.array(zMember) }).strict(),
  exampleRequest: { query: "Lovelace" },
  exampleResponse: { members: [EXAMPLE_MEMBER] },
});

/** Memberships are non-refundable: a cancel ends the membership and retires its cards; no money moves. */
export const membershipCancel = writeOp({
  name: "membership.cancel",
  permission: "entitlement:write",
  principals: [...BACK_OFFICE],
  fields: { memberNumber: zId, reason: z.string().max(200).optional() },
  response: z.object({ member: zMember }).strict(),
  exampleRequest: { memberNumber: "M12345678", reason: "Moved away" },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, status: "CANCELLED", cancelledAt: 1_795_000_000_000, cancelReason: "Moved away" } },
});

/** Named adults are locked while the term runs unless an ADMIN changes them. */
export const membershipUpdate = writeOp({
  name: "membership.update",
  permission: "entitlement:write",
  principals: [...BACK_OFFICE],
  fields: {
    memberNumber: zId,
    namedAdults: z.array(zNamedAdult).min(1).max(2).optional(),
    householdCount: z.number().int().min(0).max(10).optional(),
    addOns: z.array(zId).max(10).optional(),
    email: z.string().min(3).max(254).optional(),
  },
  response: z.object({ member: zMember }).strict(),
  exampleRequest: { memberNumber: "M12345678", householdCount: 3 },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, householdCount: 3 } },
});

/**
 * One card per named adult, valid across the term, each with its signed
 * credential (`ttk1.…`, the ticket token shape). A re-issue retires every
 * live card first. The gate learns to admit them in B704.
 */
export const memberCardIssue = writeOp({
  name: "member.card.issue",
  permission: "order:write",
  principals: [...COUNTER],
  fields: { memberNumber: zId },
  response: z
    .object({
      memberNumber: zId,
      cards: z.array(z.object({ cardId: zId, holder: z.string().min(1), validFrom: zEpochMs, validUntil: zEpochMs, token: z.string().min(1) }).strict()),
    })
    .strict(),
  exampleRequest: { memberNumber: "M12345678" },
  exampleResponse: {
    memberNumber: "M12345678",
    cards: [{ cardId: "mc-1", holder: "Ada Lovelace", validFrom: 1_791_000_000_000, validUntil: 1_822_536_000_000, token: "ttk1.eyJleGFtcGxlIjp0cnVlfQ.key-1.c2ln" }],
  },
});

/* -------------------------------------------------------------- switches */

const zSwitchFields = {
  residency: z.boolean(),
  photoId: z.boolean(),
  facial: z.boolean(),
  blackouts: z.boolean(),
  reciprocal: z.boolean(),
  guestsPerDay: z.boolean(),
  fmvOnReceipt: z.boolean(),
  graceDays: z.number().int().min(0).max(90),
  giftVoucherDays: z.number().int().min(0).max(1_095),
  /**
   * B704 (4) — the reciprocal discount in basis points (class-B default
   * 2026-10-09: the rate lives beside the switch). The get always answers it
   * (5000 when never set); a set that omits it keeps the stored rate.
   */
  reciprocalDiscountBps: z.number().int().min(0).max(10_000).optional(),
};

/**
 * B725 — `enabled` is the memberships MODULE: whether this business sells
 * memberships at all (resolved — the stored choice, else off for a SHOW
 * tenant and on for every other type). The back office and the till read
 * it to decide which screens exist; the set takes it optional, and absent
 * leaves the choice alone.
 */
const zSwitchesBody = z.object({ ...zSwitchFields, enabled: z.boolean(), source: z.enum(["defaults", "config"]), updatedAt: zEpochMs.optional() }).strict();

const EXAMPLE_SWITCHES = {
  residency: false,
  photoId: false,
  facial: false,
  blackouts: false,
  reciprocal: false,
  guestsPerDay: false,
  fmvOnReceipt: false,
  graceDays: 14,
  giftVoucherDays: 0,
} as const;

/** B709 (i) — a buyer reads the switches too (read-only): the storefront says what they govern — the ZIP row, the photo-ID sentence — truthfully or not at all. */
export const membershipSettingsGet = readOp({
  name: "membership.settings.get",
  permission: "catalog:read",
  principals: [...COUNTER, "CUSTOMER"],
  fields: {},
  response: zSwitchesBody,
  exampleRequest: {},
  exampleResponse: { ...EXAMPLE_SWITCHES, reciprocalDiscountBps: 5_000, enabled: true, source: "defaults" },
});

export const membershipSettingsSet = writeOp({
  name: "membership.settings.set",
  // Tenant configuration write — the channel-config scope (ADMIN-only), the ops.config.set wall.
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { ...zSwitchFields, enabled: z.boolean().optional() },
  response: zSwitchesBody,
  exampleRequest: { ...EXAMPLE_SWITCHES, residency: true, graceDays: 30 },
  exampleResponse: { ...EXAMPLE_SWITCHES, residency: true, graceDays: 30, enabled: true, source: "config", updatedAt: 1_791_000_000_000 },
});

/* ------------------------------------------------------------- blackouts */

/**
 * B704 (2) — the tenant's blackout calendars: named lists of VENUE days a
 * tier points at (`admission.blackoutCalendarId`). The gate reads them only
 * while the `blackouts` switch is on. Replaced whole on every set (≤ 20
 * calendars × ≤ 366 days); the domain judges each by name (422 with the
 * sentences), the schema only bounds the shape.
 */
const zBlackoutCalendar = z
  .object({
    calendarId: zId,
    name: z.string().max(80),
    days: z.array(z.object({ date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/), reason: z.string().max(80) }).strict()).max(366),
  })
  .strict();

const zBlackoutsBody = z.object({ calendars: z.array(zBlackoutCalendar).max(20), updatedAt: zEpochMs.optional() }).strict();

const EXAMPLE_CALENDAR = { calendarId: "events", name: "Special events", days: [{ date: "2026-10-31", reason: "Halloween Spooktacular" }] };

export const membershipBlackoutsGet = readOp({
  name: "membership.blackouts.get",
  permission: "catalog:read",
  principals: [...COUNTER],
  fields: {},
  response: zBlackoutsBody,
  exampleRequest: {},
  exampleResponse: { calendars: [EXAMPLE_CALENDAR] },
});

export const membershipBlackoutsSet = writeOp({
  name: "membership.blackouts.set",
  // Tenant configuration write — the settings.set wall (ADMIN-only, channel:write).
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { calendars: z.array(zBlackoutCalendar).max(20) },
  response: zBlackoutsBody,
  exampleRequest: { calendars: [EXAMPLE_CALENDAR] },
  exampleResponse: { calendars: [EXAMPLE_CALENDAR], updatedAt: 1_791_000_000_000 },
});

/**
 * B710 (c) — spend one of the term's guest passes for a guest the counter
 * admits now (641:2's GUEST PASS tile). Refused in words when none are
 * left or the membership is not live. A member-record write: no order, no
 * money, no ledger. (Placed LAST on purpose: app-drift's B312 collector reads
 * 600 chars past each `response: z.object(`, and a short schema just above
 * the switches' booleans enrolled them as outcomes of this op.)
 */
export const memberGuestPassSpend = writeOp({
  name: "member.guestpass.spend",
  permission: "order:write",
  principals: [...COUNTER],
  fields: { memberNumber: zId },
  response: z.object({ memberNumber: zId, guestPasses: z.number().int().min(0) }).strict(),
  exampleRequest: { memberNumber: "M12345678" },
  exampleResponse: { memberNumber: "M12345678", guestPasses: 1 },
});

/**
 * B710 (a) — the counter takes a membership's money (641:2's TENDER): a
 * renewal and/or add-ons, paid in cash or on the card reader (staff-attested,
 * as `payment.record_external` is). The server prices the sale; `amount` is
 * the till's total and a stale one is refused with both numbers. The charge
 * posts B703 (2b)'s `membership_charge` on the tender's settlement account
 * BEFORE the member moves; `saleId` (the till's, one per press) derives the
 * journal id, so a re-sent sale charges once. (Placed last: see the note on
 * member.guestpass.spend above.)
 */
export const membershipSale = writeOp({
  name: "membership.sale",
  permission: "payment:write",
  principals: [...COUNTER],
  fields: {
    memberNumber: zId,
    saleId: zId,
    renew: z.boolean(),
    addOns: z.array(zId).max(10),
    /** B710 (a2) — a new membership's first term (sold moments ago by membership.create); charged once per member, whatever the sale id. */
    join: z.boolean().optional(),
    /** B703 (2d) — move to a dearer (or equal-price) tier now, for the difference prorated by whole days left; its own sale. */
    upgradeTo: zId.optional(),
    tender: z.enum(["CASH", "TERMINAL"]),
    amount: zNonNegMoney,
  },
  response: z.object({ member: zMember, saleId: zId, charged: zNonNegMoney }).strict(),
  exampleRequest: { memberNumber: "M12345678", saleId: "msale-1", renew: true, addOns: [], tender: "CASH", amount: { amount: 18_900, currency: "USD" } },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, term: { start: 1_822_536_000_000, end: 1_854_072_000_000 } }, saleId: "msale-1", charged: { amount: 18_900, currency: "USD" } },
});

/**
 * B703 (2d) — queue a cheaper tier for the next renewal (`tierId`), or clear
 * the queue (`null`, or the current tier). No money moves and the tier stays
 * until the renewal, whichever door takes it, prices and applies the queued
 * one; a dearer tier is refused (an upgrade is a till sale, membership.sale
 * `upgradeTo`). (Placed last: see the note on member.guestpass.spend above.)
 */
export const membershipDowngrade = writeOp({
  name: "membership.downgrade",
  permission: "order:write",
  principals: [...COUNTER],
  fields: { memberNumber: zId, tierId: zId.nullable() },
  response: z.object({ member: zMember }).strict(),
  exampleRequest: { memberNumber: "M12345678", tierId: "individual" },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, renewal: { method: "MANUAL", pendingTierId: "individual" } } },
});

/**
 * B704 (4) — reciprocal admission (AZA) at the gate: another institution's
 * member shows their card. Behind the tenant's `reciprocal` switch; records
 * the visit (one scan per head, the institution and the card number only —
 * never a name) and answers the tenant's discount in basis points. Refused
 * in words (409) while the switch is off or for an unreadable card; a
 * retried press (same `redemptionId`) admits nobody twice. The gate crew's
 * door (redeem:execute), like every other admit. (Placed last: see the note
 * on member.guestpass.spend above.)
 */
export const reciprocalAdmit = writeOp({
  name: "reciprocal.admit",
  permission: "redeem:execute",
  principals: ["STAFF:VALIDATOR", "STAFF:MANAGER", "STAFF:ADMIN", "DEVICE:VALIDATOR"],
  fields: {
    institution: z.string().min(1).max(120),
    cardNumber: z.string().min(1).max(80),
    partySize: z.number().int().min(1).max(99),
    redemptionId: zId,
    deviceId: zId,
    staffId: zId,
    at: zEpochMs,
  },
  response: z.object({ admitted: z.number().int().positive(), discountBps: z.number().int().min(0).max(10_000) }).strict(),
  exampleRequest: { institution: "Phoenix Zoo", cardNumber: "PZ 1234 5678", partySize: 3, redemptionId: "rcp-1", deviceId: "gate-1", staffId: "staff-1", at: 1_791_000_000_000 },
  exampleResponse: { admitted: 3, discountBps: 5_000 },
});

/**
 * B704 (4b) — what the gate needs before it offers 675:2's reciprocal face:
 * whether the tenant's switch is on and the rate the face prints. Gate crew
 * only, under the gate's own `redeem:read` (a validator holds no
 * `catalog:read`, so `membership.settings.get` stays the counter's and the
 * storefront's); it answers the rate while the switch is on, null while off,
 * and nothing else.
 */
export const reciprocalTerms = readOp({
  name: "reciprocal.terms",
  permission: "redeem:read",
  principals: ["STAFF:VALIDATOR", "STAFF:MANAGER", "STAFF:ADMIN", "DEVICE:VALIDATOR"],
  fields: {},
  /** The rate while the switch is on; null = reciprocal admission is off (no face is offered). */
  response: z.object({ discountBps: z.number().int().min(0).max(10_000).nullable() }).strict(),
  exampleRequest: {},
  exampleResponse: { discountBps: 5_000 },
});

/**
 * B709 (ii) — the buyer's own door (636:2's PAY), on the design seat's
 * 2026-10-09 default. CUSTOMER only, owner-fenced: a signed-in buyer buys
 * under their own email, a guest names the email the membership is for (as
 * cart.checkout's buyerEmail). The server prices the first term (tier +
 * add-ons) and refuses a stale `amount` with both numbers. With no card saved
 * for this `buyId` yet the answer is the gateway's hosted card page
 * (`setupUrl`) and nothing is written; once one is saved the card is charged
 * through B703's PaymentGatewayPort on the connected account (TEST keys only —
 * the B23a wall), `membership_charge` posts BEFORE the member exists, and the
 * member is minted with its cards (B711's welcome rides the carding) and,
 * when `autoRenew`, renewing AUTO on that card in the buyer's `consentText`.
 * A refused card answers `refused` (a sentence) with the card page again and
 * writes nothing. One `buyId` is one purchase: a retry moves money once and
 * mints once. Reversal = unregister the op. (Placed last: see the note on
 * member.guestpass.spend above.)
 */
export const membershipBuy = writeOp({
  name: "membership.buy",
  owner: "customer",
  permission: "order:write",
  principals: ["CUSTOMER"],
  fields: {
    buyId: zId,
    planId: zId,
    tierId: zId,
    namedAdults: z.array(zNamedAdult).min(1).max(2),
    householdCount: z.number().int().min(0).max(10),
    addOns: z.array(zId).max(10),
    buyerEmail: z.string().min(3).max(254),
    zip: z.string().min(3).max(10).optional(),
    autoRenew: z.boolean(),
    consentText: z.string().max(500).optional(),
    returnUrl: z.string().url().max(2048),
    amount: zNonNegMoney,
  },
  response: z.object({ member: zMember.optional(), setupUrl: z.string().optional(), refused: z.string().optional(), charged: zNonNegMoney.optional() }).strict(),
  exampleRequest: {
    buyId: "buy-1",
    planId: "annual",
    tierId: "family",
    namedAdults: [{ name: "Ada Lovelace" }, { name: "Charles Babbage" }],
    householdCount: 2,
    addOns: [],
    buyerEmail: "ada@example.org",
    autoRenew: true,
    consentText: "Renew my membership each year with this card until I turn it off.",
    returnUrl: "https://store.example.org/memberships?tenant=t-1&buy=buy-1",
    amount: { amount: 18_900, currency: "USD" },
  },
  exampleResponse: { member: { ...EXAMPLE_MEMBER, renewal: { method: "AUTO" } }, charged: { amount: 18_900, currency: "USD" } },
});

/**
 * B703 (7c) — a member's payments, read off the books for 638:420's PAYMENTS
 * card and the back office's refund face over `membership.refund`. Every
 * `membership_charge` journalled for the member (`mship:<M>:<paymentId>`),
 * newest first: what it charged, what has gone back, what is LEFT to refund,
 * and who took it — CARD (the gateway; the refund door can give it back),
 * COUNTER (`till:<saleId>`; the counter gives it back) or UNLINKED (a first
 * term journalled under `join`; no gateway payment to ask about). `deferred`
 * is the revenue it still holds and `monthly` its recognition tranche (the
 * card's foot sentence). The books' audience — refund.list's: MANAGER and
 * ADMIN on `ledger:read`; a read, nothing written, no gateway call.
 */
export const zMemberPayment = z
  .object({
    paymentId: zId,
    takenBy: z.enum(["CARD", "COUNTER", "UNLINKED"]),
    at: zEpochMs,
    term: z.object({ start: zEpochMs, end: zEpochMs }).strict(),
    charged: zNonNegMoney,
    refunded: zNonNegMoney,
    left: zNonNegMoney,
    deferred: zNonNegMoney,
    monthly: zNonNegMoney,
  })
  .strict();
export const membershipPaymentsList = readOp({
  name: "membership.payments.list",
  permission: "ledger:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: { memberNumber: zId },
  response: z.object({ payments: z.array(zMemberPayment) }).strict(),
  exampleRequest: { memberNumber: "M12345678" },
  exampleResponse: {
    payments: [
      {
        paymentId: "pi_123",
        takenBy: "CARD",
        at: 1_791_000_000_000,
        term: { start: 1_791_000_000_000, end: 1_822_536_000_000 },
        charged: { amount: 18_900, currency: "USD" },
        refunded: { amount: 0, currency: "USD" },
        left: { amount: 18_900, currency: "USD" },
        deferred: { amount: 18_900, currency: "USD" },
        monthly: { amount: 1_575, currency: "USD" },
      },
    ],
  },
});

/**
 * B703 (b) — the staff refund door. RECORDED DEFAULT 2026-10-09 (cowork-fable,
 * design seat, under Pathik's 2026-10-06 instruction — he overrules with "no
 * on B703"; reversal = unregister this op). ADMIN only on `payment:write`.
 * Gives back part or all of ONE card payment a member made (`paymentId`, the
 * gateway's — the charge journal `mship:<memberNumber>:<paymentId>`), capped
 * at what is left on it, through the gateway's `refund`, and posts
 * `membership_refund` (deferred first). A payment the counter took
 * (`till:<saleId>`) is refused: the counter gives it back; so is a first
 * term journalled under `join` (no gateway payment id to refund). Every
 * refusal is in words and writes nothing. `refundRequestId` is the press: a
 * re-sent one refunds once. PENDING = the gateway accepted it but has not
 * settled; its `refunded` webhook books it. (Placed last: see the note on
 * member.guestpass.spend above.)
 */
export const membershipRefund = writeOp({
  name: "membership.refund",
  permission: "payment:write",
  principals: ["STAFF:ADMIN"],
  fields: { memberNumber: zId, paymentId: zId, refundRequestId: zId, amount: zPositiveMoney },
  response: z
    .object({ memberNumber: zId, paymentId: zId, status: z.enum(["REFUNDED", "PENDING"]), refunded: zNonNegMoney, left: zNonNegMoney })
    .strict(),
  exampleRequest: { memberNumber: "M12345678", paymentId: "pi_123", refundRequestId: "mrf-1", amount: { amount: 5_000, currency: "USD" } },
  exampleResponse: { memberNumber: "M12345678", paymentId: "pi_123", status: "REFUNDED", refunded: { amount: 5_000, currency: "USD" }, left: { amount: 13_900, currency: "USD" } },
});
